// Row for the Applications table. Stage cell is an inline <select> so the
// user can change status without opening the detail page. Clicking anywhere
// else on the row still navigates to detail.

import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { Trash2, X, Activity, RefreshCw } from 'lucide-react';
import Chip, { stageVariant } from '../ui/Chip';
import { useZh } from '../../lib/useProduct';
import { stageLabel } from '../../lib/stageLabels';
import type { Application, ApplicationStage } from '../../lib/types';

// Per-row liveness check (Phase 14j). Hits GET /api/liveness?id=N which
// uses the existing liveness-core.mjs Playwright helper. Result is cached
// in localStorage for 24h so re-renders don't spam the endpoint and the
// user sees the last known status when scrolling back to a row they
// already checked.
type LivenessState = 'unknown' | 'live' | 'expired' | 'checking' | 'error';
interface LivenessCacheEntry {
  state: LivenessState;
  ts: number;
}
const LIVENESS_TTL_MS = 24 * 60 * 60 * 1000;
function livenessCacheKey(num: number): string {
  return `nice2job.liveness.${num}`;
}
function loadLivenessCache(num: number): LivenessCacheEntry | null {
  try {
    const raw = localStorage.getItem(livenessCacheKey(num));
    if (!raw) return null;
    const parsed = JSON.parse(raw) as LivenessCacheEntry;
    if (Date.now() - parsed.ts > LIVENESS_TTL_MS) return null;
    return parsed;
  } catch { return null; }
}
function saveLivenessCache(num: number, state: LivenessState) {
  try {
    localStorage.setItem(livenessCacheKey(num), JSON.stringify({ state, ts: Date.now() }));
  } catch { /* noop */ }
}

// Stage-aware next-step CTA — maps each pipeline stage to the single most
// useful action a candidate takes at that point. Clicking jumps straight to
// the recommended module.
function nextAction(stage: ApplicationStage, id: string, zh: boolean): { label: string; to: string } | null {
  switch (stage) {
    case 'Evaluated':
      // Apply helper lives inside ApplicationDetail — deep-link straight into
      // the Apply tab rather than routing through a separate /apply/:id page.
      return { label: zh ? '去申请 →' : 'Apply →', to: `/apps/${encodeURIComponent(id)}?tab=apply` };
    case 'Applied':
    case 'Responded':
      return { label: zh ? '跟进 →' : 'Follow up →', to: `/apps/${encodeURIComponent(id)}` };
    case 'Phone screen':
    case 'Interview':
      return { label: zh ? '面试准备 →' : 'Prep →', to: `/interview/${encodeURIComponent(id)}/prep` };
    case 'Offer':
      return { label: zh ? '谈判 →' : 'Negotiate →', to: `/negotiate/${encodeURIComponent(id)}` };
    default:
      return null;
  }
}

const ALL_STAGES: ApplicationStage[] = [
  'Evaluated',
  'Applied',
  'Responded',
  'Phone screen',
  'Interview',
  'Offer',
  'Rejected',
  'Discarded',
  'SKIP',
];

interface Props {
  app: Application;
  onOpen?: (a: Application) => void;
}

export default function ApplicationRow({ app: a, onOpen }: Props) {
  const zh = useZh();
  const nav = useNavigate();
  const qc = useQueryClient();
  const [stage, setStage] = useState<ApplicationStage>(a.stage);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [armed, setArmed] = useState(false);
  const [deleting, setDeleting] = useState(false);

  // Per-row liveness — hydrate from 24h localStorage cache on mount, then
  // user can refresh on demand. We don't auto-fetch on mount because the
  // /api/liveness endpoint is Playwright-backed (~3-8s per check); a
  // pipeline of 50 apps would otherwise hammer it.
  const [liveness, setLiveness] = useState<LivenessState>(
    () => loadLivenessCache(a.num)?.state ?? 'unknown',
  );
  const checkLiveness = async (e: React.MouseEvent) => {
    e.stopPropagation();
    if (liveness === 'checking') return;
    setLiveness('checking');
    try {
      const r = await fetch(`/api/liveness?id=${encodeURIComponent(String(a.num))}`);
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      const j = await r.json();
      // Server returns canonical {state: 'live'|'expired'|'unknown'} since
      // the liveness-sweep refactor. Fall back to the legacy `result`
      // ('active'|'expired'|'uncertain') for old deployments where this
      // SPA might be served against an older server build.
      const raw = j.state ?? (j.result === 'active' ? 'live' : j.result);
      const s: LivenessState =
        raw === 'live' ? 'live'
        : raw === 'expired' ? 'expired'
        : 'unknown';
      setLiveness(s);
      saveLivenessCache(a.num, s);
    } catch {
      setLiveness('error');
    }
  };

  const rowId = a.id || String(a.num);
  const next = nextAction(stage, rowId, zh);

  // Auto-revert armed delete after 3s (matches MatchRow / CVPicker pattern)
  useEffect(() => {
    if (!armed) return;
    const t = setTimeout(() => setArmed(false), 3000);
    return () => clearTimeout(t);
  }, [armed]);

  const handleDelete = async (e: React.MouseEvent) => {
    e.stopPropagation();
    if (!armed) {
      setArmed(true);
      return;
    }
    setArmed(false);
    setDeleting(true);
    try {
      const ids = encodeURIComponent(String(a.num));
      const res = await fetch(`/api/applications/lines?ids=${ids}`, { method: 'DELETE' });
      const j = await res.json();
      if (res.ok && j.success) {
        qc.invalidateQueries({ queryKey: ['data'] });
      } else {
        alert('Delete failed: ' + (j.error || 'unknown'));
      }
    } catch (err) {
      alert('Delete failed: ' + (err as Error).message);
    } finally {
      setDeleting(false);
    }
  };

  // Transient toast-style hint shown when stage flips to Offer, so the user
  // sees that the row is now linked to the Offers page.
  const [syncedToast, setSyncedToast] = useState(false);
  useEffect(() => {
    if (!syncedToast) return;
    const t = setTimeout(() => setSyncedToast(false), 4000);
    return () => clearTimeout(t);
  }, [syncedToast]);

  const changeStage = async (next: ApplicationStage) => {
    if (next === stage || saving) return;
    const prev = stage;
    setStage(next); // optimistic
    setSaving(true);
    setError(null);
    try {
      const res = await fetch('/api/applications/status', {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ id: String(a.num), status: next }),
      });
      const j = await res.json();
      if (!res.ok || !j.success) {
        throw new Error(j.error || 'patch_failed');
      }
      // Keep data queries in sync so the stat tiles above refresh too
      qc.invalidateQueries({ queryKey: ['data'] });
      // Surface the Applications → Offers link the moment the user promotes
      // the row. Offers.tsx already merges `applications[stage=Offer]` into
      // its card list — this toast just makes the link visible.
      if (next === 'Offer') setSyncedToast(true);
    } catch (e) {
      setStage(prev); // rollback
      setError((e as Error).message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div
      className="row"
      style={{ gridTemplateColumns: '1.4fr 1.6fr 1fr 0.7fr 0.85fr 0.85fr 0.65fr 40px', cursor: 'pointer' }}
      onClick={() => onOpen?.(a)}
    >
      <div style={{ fontWeight: 500 }}>{a.company}</div>
      <div style={{ color: 'var(--ink2)' }}>{a.role}</div>
      <div
        style={{ position: 'relative', display: 'flex', alignItems: 'center', gap: 6 }}
        onClick={(e) => e.stopPropagation()}
        title={error ? `Save failed: ${error}` : 'Change stage directly — no page reload needed'}
      >
        <Chip variant={stageVariant(stage)} style={{ fontSize: 11.5, opacity: saving ? 0.5 : 1 }}>
          {stageLabel(stage, zh)}
        </Chip>
        {syncedToast && (
          <span
            onClick={(e) => { e.stopPropagation(); nav('/offers'); }}
            style={{
              fontSize: 10.5,
              padding: '3px 8px',
              background: 'rgba(45,122,74,0.12)',
              color: '#2d7a4a',
              border: '1px solid rgba(45,122,74,0.3)',
              borderRadius: 10,
              cursor: 'pointer',
              whiteSpace: 'nowrap',
            }}
            title="This row now appears on the Offers page. Click to jump there."
          >
            → Offers ↗
          </span>
        )}
        <select
          value={stage}
          disabled={saving}
          onChange={(e) => changeStage(e.target.value as ApplicationStage)}
          style={{
            position: 'absolute',
            inset: 0,
            width: '100%',
            height: '100%',
            opacity: 0,
            cursor: saving ? 'wait' : 'pointer',
          }}
          aria-label="Change stage"
        >
          {ALL_STAGES.map((s) => (
            <option key={s} value={s}>{stageLabel(s, zh)}</option>
          ))}
        </select>
      </div>
      <div className="num" style={{ fontSize: 18 }}>
        {a.score.toFixed(1)}
        <span style={{ fontSize: 11, color: 'var(--ink3)' }}>/5</span>
      </div>
      {/* Posting liveness — chip + refresh button. Calls /api/liveness?id=N
          (Playwright-backed) on demand, caches 24h. Replaces the abstract
          "Status" indicator that lived in Settings/portals — actually
          actionable here because it tells you per-app whether the JD URL
          still resolves. */}
      <div onClick={(e) => e.stopPropagation()} style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
        {liveness === 'live' && <Chip variant="good" style={{ fontSize: 10.5 }}>{zh ? '有效' : 'Live'}</Chip>}
        {liveness === 'expired' && <Chip variant="warn" style={{ fontSize: 10.5 }}>{zh ? '已过期' : 'Expired'}</Chip>}
        {liveness === 'unknown' && <span style={{ fontSize: 11, color: 'var(--ink3)' }}>—</span>}
        {liveness === 'error' && <span style={{ fontSize: 10.5, color: 'var(--warn)' }} title="Liveness check failed — try again">err</span>}
        {liveness === 'checking' && <span style={{ fontSize: 10.5, color: 'var(--ink3)' }}>…</span>}
        <button
          className="btn ghost"
          onClick={checkLiveness}
          disabled={liveness === 'checking'}
          title={liveness === 'unknown' ? 'Check if the job posting is still live (Playwright check, ~5s)' : 'Re-check (last result cached 24h)'}
          style={{ padding: '2px 4px', border: 'none', minWidth: 0 }}
          aria-label="Check posting liveness"
        >
          {liveness === 'unknown' ? <Activity size={12} color="var(--ink3)" /> : <RefreshCw size={11} color="var(--ink3)" />}
        </button>
      </div>
      <div style={{ color: 'var(--ink3)', fontSize: 12 }}>{a.date}</div>
      <div
        style={{ textAlign: 'right' }}
        onClick={(e) => e.stopPropagation()}
      >
        {next ? (
          <button
            className="btn"
            onClick={() => nav(next.to)}
            style={{ padding: '4px 10px', fontSize: 11 }}
            title={`Jump to the recommended next step for "${stage}"`}
          >
            {next.label}
          </button>
        ) : (
          <span style={{ color: 'var(--ink3)', fontSize: 12 }}>—</span>
        )}
      </div>
      <div
        style={{ textAlign: 'right' }}
        onClick={(e) => e.stopPropagation()}
      >
        <button
          className="btn ghost"
          onClick={handleDelete}
          disabled={deleting}
          title={armed ? 'Click again to delete this row from applications.md' : 'Delete this application'}
          aria-label={armed ? 'Confirm delete' : 'Delete'}
          style={{
            padding: armed ? '4px 8px' : '4px 6px',
            fontSize: 11,
            lineHeight: armed ? 1 : 0,
            display: 'inline-flex',
            alignItems: 'center',
            gap: 4,
            color: armed ? '#fff' : '#b43a3a',
            background: armed ? '#b43a3a' : 'transparent',
            border: armed ? '1px solid #b43a3a' : undefined,
            transition: 'all 120ms',
            opacity: deleting ? 0.5 : 1,
          }}
        >
          {armed ? (<><X size={12} /> Sure?</>) : <Trash2 size={13} />}
        </button>
      </div>
    </div>
  );
}
